import React from "react";
import { observer } from "mobx-react";
import { Box, CircularProgress, Grid2, Paper, useTheme } from "@mui/material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import { DailyTasksStore } from "../../../../stores/dashboards/DailyTasksStore";
import { formatOpsTatCount, formatOpsTatNumber } from "../ops-tat/opsTatFormat";
import {
  DAILY_TASKS_ACTIVITY_METRIC,
  DailyTasksTatActivity,
  DailyTasksTatMetricId,
} from "../../../../../../models/Dashboard/DailyTasks/DailyTasksTat";
import {
  DailyTasksHint,
  DailyTasksSectionTitle,
  DAILY_TASKS_HINTS,
} from "./DailyTasksHint";
import { dailyTasksStageUi } from "./DailyTasksStageUi";

interface DailyTasksActivityProps {
  store: DailyTasksStore;
}

const MOCKUP_TILES: Array<{
  key: keyof DailyTasksTatActivity;
  label: string;
  detail: string;
}> = [
  {
    key: "logos_uploaded",
    label: "Logos uploaded",
    detail: "Client logos in this window",
  },
  {
    key: "mockups_uploaded",
    label: "Mockups uploaded",
    detail: "Files created in this window",
  },
  {
    key: "products_touched",
    label: "Products with upload",
    detail: "Distinct products uploaded",
  },
  {
    key: "uploads_per_product",
    label: "Uploads / product",
    detail: "Mean files per product uploaded",
  },
  {
    key: "changes_requested",
    label: "Rework",
    detail: "Changes requested in this window",
  },
  {
    key: "mockups_rejected",
    label: "Rejected",
    detail: "Rejected in this window",
  },
  {
    key: "mockups_approved",
    label: "Approved",
    detail: "Approved in this window",
  },
  {
    key: "mockups_deleted",
    label: "Deleted",
    detail: "Soft-deleted in this window",
  },
];

const PR_TILES: Array<{
  key: keyof DailyTasksTatActivity;
  label: string;
  detail: string;
}> = [
  {
    key: "prs_created",
    label: "PRs created",
    detail: "Approved purchase requests in this window",
  },
  {
    key: "pr_products_touched",
    label: "Products with PR",
    detail: "Distinct products with a PR",
  },
  {
    key: "covering_prs",
    label: "Covering PR",
    detail: "Products that reached clean covering PR qty",
  },
];

const PO_TILES: Array<{
  key: keyof DailyTasksTatActivity;
  label: string;
  detail: string;
}> = [
  {
    key: "pos_created",
    label: "POs created",
    detail: "Issued purchase orders in this window",
  },
  {
    key: "po_products_touched",
    label: "Products with PO",
    detail: "Distinct products with a PO",
  },
  {
    key: "all_pos",
    label: "All POs",
    detail: "Products that got a PO for every approved PR",
  },
  {
    key: "emails_sent",
    label: "Emailed to vendor",
    detail: "POs whose vendor email was sent",
  },
  {
    key: "pos_cancelled",
    label: "POs cancelled",
    detail: "Purchase orders cancelled in this window",
  },
];

const RAW_TILES: Array<{
  key: keyof DailyTasksTatActivity;
  label: string;
  detail: string;
}> = [
  {
    key: "raw_grns",
    label: "Inwards",
    detail: "Inward logged in this window",
  },
  {
    key: "raw_full_inward",
    label: "Full inward",
    detail: "Products that hit full qty",
  },
  {
    key: "raw_qc_closed",
    label: "QC closed",
    detail: "Products whose QC closed",
  },
  {
    key: "raw_qty_rejected",
    label: "Rejected qty",
    detail: "Quantity rejected in this window",
  },
];

const PRINT_TILES: Array<{
  key: keyof DailyTasksTatActivity;
  label: string;
  detail: string;
}> = [
  {
    key: "print_sends",
    label: "Outwards",
    detail: "Print outwards logged in this window",
  },
  {
    key: "print_full",
    label: "Full outward",
    detail: "Products that reached full outward qty",
  },
  {
    key: "print_qty",
    label: "Qty outward",
    detail: "Incremental qty outward in this window",
  },
];

const PRINT_IN_TILES: Array<{
  key: keyof DailyTasksTatActivity;
  label: string;
  detail: string;
}> = [
  {
    key: "print_inwards",
    label: "Inwards",
    detail: "Print inwards logged in this window",
  },
  {
    key: "print_in_full",
    label: "Full inward",
    detail: "Products that hit full print inward",
  },
  {
    key: "print_in_qc_closed",
    label: "QC closed",
    detail: "Print QC cleared in this window",
  },
  {
    key: "print_in_qty_rejected",
    label: "Rejected",
    detail: "Qty rejected on print QC",
  },
];

const FILE_TILES: Array<{
  key: keyof DailyTasksTatActivity;
  label: string;
  detail: string;
}> = [
  {
    key: "files_uploaded",
    label: "Files uploaded",
    detail: "Printing files in this window",
  },
  {
    key: "file_products_touched",
    label: "Products with file",
    detail: "Distinct products with a file",
  },
  {
    key: "files_sent_to_printer",
    label: "Sent to printer",
    detail: "Marked sent in this window",
  },
];

const PROJECT_TILES: Array<{
  key: keyof DailyTasksTatActivity;
  label: string;
  detail: string;
}> = [
  {
    key: "project_requests",
    label: "Requests",
    detail: "Project requests raised in this window",
  },
  {
    key: "project_finance_decisions",
    label: "Finance",
    detail: "Finance approvals or rejections",
  },
  {
    key: "project_variants_covered",
    label: "Variants covered",
    detail: "Products newly variants-covered",
  },
  {
    key: "project_invoices",
    label: "Invoices",
    detail: "Customer invoices created",
  },
];

const DailyTasksActivity: React.FC<DailyTasksActivityProps> = observer(
  ({ store }) => {
    const theme = useTheme();
    const customColors = theme.palette.customColors;
    const activity = store.summaryResult?.data.activity;
    const stageUi = dailyTasksStageUi(store.filters.stage);
    const isFile = stageUi.ledger === "file";
    const isPr = stageUi.ledger === "pr";
    const isPo = stageUi.ledger === "po";
    const isRaw = stageUi.ledger === "raw";
    const isPrint = stageUi.ledger === "print";
    const isPrintIn = stageUi.ledger === "print_in";
    const isProject = stageUi.ledger === "project";
    const tiles = isProject
      ? PROJECT_TILES
      : isFile
        ? FILE_TILES
        : isPr
          ? PR_TILES
          : isPo
            ? PO_TILES
            : isRaw
              ? RAW_TILES
              : isPrint
                ? PRINT_TILES
                : isPrintIn
                  ? PRINT_IN_TILES
                  : MOCKUP_TILES;

    const openTile = async (
      metricId: DailyTasksTatMetricId,
      cardTitle?: string,
    ) => {
      try {
        await store.openDrawer(metricId, {
          cardTitle,
          cardSection: "activity",
        });
      } catch (error) {
        // drilldownError is set on the store
      }
    };

    const onTileKeyDown = (
      event: React.KeyboardEvent,
      metricId: DailyTasksTatMetricId,
    ) => {
      try {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          void openTile(metricId);
        }
      } catch (error) {
        // ignore keyboard handler failures
      }
    };

    return (
      <Paper
        sx={{
          mt: 3,
          p: { xs: 2, md: 3 },
          borderRadius: 2,
          boxShadow:
            "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
        }}
      >
        <DailyTasksSectionTitle
          title="Activity"
          hint={stageUi.activityHint}
          subtitle="Click a tile to see the rows. Only projects in the date range are included; tile counts use when the event happened on those projects."
        />

        {store.summaryLoading && !store.summaryResult ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="6rem"
          >
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Grid2 container spacing={2} columns={12}>
            {tiles.map((tile) => {
              const metricId = DAILY_TASKS_ACTIVITY_METRIC[tile.key];
              return (
                <Grid2
                  key={tile.key}
                  size={{
                    xs: 12,
                    sm: 6,
                    md: 4,
                    lg: isPo || isRaw || isPrintIn ? 3 : isFile || isPr ? 4 : 2,
                  }}
                >
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      void openTile(metricId, tile.label);
                    }}
                    onKeyDown={(event) => onTileKeyDown(event, metricId)}
                    sx={{
                      p: 1.5,
                      height: "100%",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      cursor: "pointer",
                      "&:hover": {
                        borderColor: customColors.purpleMain,
                      },
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <CustomTypography
                        variant="caption"
                        color={customColors.greyTextColor}
                        sx={{
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                        }}
                      >
                        {tile.label}
                      </CustomTypography>
                      <DailyTasksHint text={DAILY_TASKS_HINTS[tile.key]} />
                    </Box>
                    <CustomTypography
                      variant="heading3"
                      color={customColors.navyText}
                      sx={{ mt: 0.5, letterSpacing: "-0.02em" }}
                    >
                      {tile.key === "uploads_per_product"
                        ? activity?.[tile.key] == null
                          ? "—"
                          : formatOpsTatNumber(activity[tile.key], 1)
                        : formatOpsTatCount(activity?.[tile.key] ?? 0)}
                    </CustomTypography>
                    <CustomTypography
                      variant="caption"
                      color={customColors.greyTextColor}
                    >
                      {tile.detail}
                    </CustomTypography>
                  </Box>
                </Grid2>
              );
            })}
          </Grid2>
        )}
      </Paper>
    );
  },
);

export default DailyTasksActivity;
